import { useEffect, useState } from "react";
import { Send, Loader2, AlertTriangle, Smartphone, MousePointerClick, CheckCircle2, XCircle, Clock, X } from "lucide-react";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import adminApi, { apiError } from "@/services/adminApi";

const toUtc = (local) => (local ? new Date(local).toISOString().slice(0, 16) : null);
const toLocal = (utc) => {
  if (!utc) return "";
  const d = new Date(utc + "Z");
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const inputCls = "w-full px-3 py-2 bg-zinc-800/80 border border-zinc-700/40 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500/50";

const PLATFORM_LABELS = { web: "Web", android: "Android", ios: "iOS", tv: "TV" };
const PLATFORM_COLORS = { web: "bg-amber-400", android: "bg-emerald-400", ios: "bg-sky-400", tv: "bg-violet-400" };

function StatCard({ icon: Icon, label, value, sub, accent, testId }) {
  return (
    <div data-testid={testId} className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${accent}`} />
        <span className="text-xs text-zinc-500">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {sub && <p className="text-[11px] text-zinc-600 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function PushPage() {
  const [data, setData] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [linkType, setLinkType] = useState("none");
  const [linkValue, setLinkValue] = useState("");
  const [channels, setChannels] = useState([]);
  const [schedule, setSchedule] = useState("");

  const load = () => adminApi.pushAnalytics().then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (linkType === "live" && channels.length === 0) {
      adminApi.liveChannels().then(({ data }) => setChannels(data.channels || [])).catch(() => {});
    }
    setLinkValue("");
  }, [linkType]);

  const buildLink = () => {
    if (linkType === "live" && linkValue) return `/app/live?channel=${linkValue}`;
    if (linkType === "vod" && linkValue) return `/app/player/vod/${linkValue}`;
    if (linkType === "url" && linkValue) return linkValue;
    return "";
  };

  const handleSend = async () => {
    if (!title || !body) {
      toast.error("Başlıq və mətn mütləqdir");
      return;
    }
    setSending(true);
    try {
      const { data: res } = await adminApi.sendPush({ title, body, link: buildLink(), scheduled_at: schedule ? toUtc(schedule) : null });
      if (res.status === "scheduled") {
        toast.success(`Planlaşdırıldı: ${toLocal(res.scheduled_at).replace("T", " ")}`);
      } else {
        toast.success(`Göndərildi: ${res.success_count} uğurlu, ${res.failure_count} uğursuz`);
      }
      setTitle("");
      setBody("");
      setLinkType("none");
      setLinkValue("");
      setSchedule("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const handleCancelScheduled = async (id) => {
    try {
      await adminApi.cancelScheduledPush(id);
      toast.success("Planlanmış bildiriş ləğv edildi");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const s = data?.summary;
  const platformTotal = Object.values(data?.by_platform || {}).reduce((a, b) => a + b, 0) || 1;

  return (
    <div data-testid="admin-push-page">
      <h1 className="text-2xl font-bold mb-1">Push Bildirişlər</h1>
      <p className="text-sm text-zinc-500 mb-6">Göndəriş, çatdırılma və açılma statistikası (son 30 gün)</p>

      {data && !data.configured && (
        <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 max-w-2xl" data-testid="push-not-configured">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-200/90">
            <p className="font-semibold text-amber-300 mb-1">Firebase konfiqurasiya olunmayıb</p>
            <p>Service account JSON faylını <code>/app/backend/secrets/firebase-admin.json</code> yoluna qoyun.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard testId="push-stat-tokens" icon={Smartphone} label="Qeydiyyatlı cihaz" value={data?.total_tokens ?? "—"} accent="text-amber-400" />
        <StatCard testId="push-stat-sent" icon={Send} label="Göndərilən" value={s?.sent ?? "—"} accent="text-zinc-300" />
        <StatCard testId="push-stat-delivered" icon={CheckCircle2} label="Çatdırılan" value={s?.delivered ?? "—"} accent="text-emerald-400" />
        <StatCard testId="push-stat-failed" icon={XCircle} label="Uğursuz" value={s?.failed ?? "—"} accent="text-red-400" />
        <StatCard testId="push-stat-ctr" icon={MousePointerClick} label="Açılma (CTR)" value={s ? `${s.ctr}%` : "—"} sub={s ? `${s.opens} klik` : ""} accent="text-sky-400" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5" data-testid="push-trend-chart">
          <h2 className="font-semibold mb-3">30 günlük trend</h2>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.daily || []} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gDeliv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gOpen" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#38bdf8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval={4} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#71717a", fontSize: 10 }} allowDecimals={false} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#a1a1aa" }} />
                <Area type="monotone" dataKey="delivered" name="Çatdırılan" stroke="#f59e0b" fill="url(#gDeliv)" strokeWidth={2} />
                <Area type="monotone" dataKey="opens" name="Açılma" stroke="#38bdf8" fill="url(#gOpen)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5" data-testid="push-platform-breakdown">
          <h2 className="font-semibold mb-4">Platforma bölgüsü</h2>
          {Object.keys(data?.by_platform || {}).length === 0 && <p className="text-sm text-zinc-500">Cihaz yoxdur</p>}
          <div className="space-y-4">
            {Object.entries(data?.by_platform || {}).map(([platform, count]) => (
              <div key={platform}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-300">{PLATFORM_LABELS[platform] || platform}</span>
                  <span className="text-zinc-500">{count} · {Math.round((count / platformTotal) * 100)}%</span>
                </div>
                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${PLATFORM_COLORS[platform] || "bg-zinc-400"}`} style={{ width: `${(count / platformTotal) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 space-y-3 h-fit">
          <h2 className="font-semibold">Yeni bildiriş</h2>
          <input data-testid="push-title-input" placeholder="Başlıq" value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
          <textarea data-testid="push-body-input" placeholder="Mətn" rows={4} value={body} onChange={(e) => setBody(e.target.value)} className={inputCls} />
          <div>
            <label className="text-xs text-zinc-500 font-semibold block mb-1.5">Klikləyəndə açılsın (link)</label>
            <select data-testid="push-link-type" value={linkType} onChange={(e) => setLinkType(e.target.value)} className={inputCls}>
              <option value="none">Link yoxdur</option>
              <option value="live">Canlı kanal</option>
              <option value="vod">Film (VOD)</option>
              <option value="url">Xüsusi URL</option>
            </select>
          </div>
          {linkType === "live" && (
            <select data-testid="push-link-channel" value={linkValue} onChange={(e) => setLinkValue(e.target.value)} className={inputCls}>
              <option value="">Kanal seçin...</option>
              {channels.map((ch) => <option key={ch.stream_id} value={ch.stream_id}>{ch.name}</option>)}
            </select>
          )}
          {linkType === "vod" && (
            <input data-testid="push-link-vod" placeholder="Film stream_id" value={linkValue} onChange={(e) => setLinkValue(e.target.value)} className={inputCls} />
          )}
          {linkType === "url" && (
            <input data-testid="push-link-url" placeholder="https://... və ya /app/..." value={linkValue} onChange={(e) => setLinkValue(e.target.value)} className={inputCls} />
          )}
          <div>
            <label className="text-xs text-zinc-500 font-semibold block mb-1.5">Planla (boş buraxsanız dərhal göndərilir)</label>
            <input data-testid="push-schedule-input" type="datetime-local" value={schedule} onChange={(e) => setSchedule(e.target.value)} className={inputCls} />
          </div>
          <button
            data-testid="push-send-btn"
            onClick={handleSend}
            disabled={sending || !data?.configured || (!schedule && !data?.total_tokens)}
            className="flex items-center gap-2 px-5 py-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-semibold rounded-lg text-sm transition-colors"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : schedule ? <Clock className="w-4 h-4" /> : <Send className="w-4 h-4" />} {schedule ? "Planla" : `Göndər (${data?.total_tokens ?? 0} cihaz)`}
          </button>
          {data?.scheduled?.length > 0 && (
            <div className="pt-3 border-t border-zinc-800/60" data-testid="push-scheduled-list">
              <p className="text-xs text-zinc-500 font-semibold mb-2 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-blue-400" /> Planlanmış bildirişlər</p>
              <div className="space-y-1.5">
                {data.scheduled.map((sc) => (
                  <div key={sc.id} className="flex items-center justify-between bg-zinc-800/40 border border-blue-500/20 rounded-lg px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">{sc.title}</p>
                      <p className="text-[10px] text-blue-400">{toLocal(sc.scheduled_at).replace("T", " ")}</p>
                    </div>
                    <button data-testid={`push-cancel-${sc.id}`} onClick={() => handleCancelScheduled(sc.id)} className="text-zinc-500 hover:text-red-400 transition-colors ml-2 flex-shrink-0">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5" data-testid="push-campaign-table">
          <h2 className="font-semibold mb-3">Kampaniya tarixçəsi</h2>
          {(!data || data.campaigns.length === 0) && <p className="text-sm text-zinc-500">Hələ bildiriş göndərilməyib</p>}
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-zinc-500 sticky top-0 bg-zinc-900">
                <tr>
                  <th className="py-2 pr-3 font-medium">Bildiriş</th>
                  <th className="py-2 pr-3 font-medium">Tarix</th>
                  <th className="py-2 pr-3 font-medium text-right">Çatdı</th>
                  <th className="py-2 pr-3 font-medium text-right">Uğursuz</th>
                  <th className="py-2 pr-3 font-medium text-right">Açılma</th>
                  <th className="py-2 font-medium text-right">CTR</th>
                </tr>
              </thead>
              <tbody>
                {data?.campaigns.map((c) => {
                  const ctr = c.success_count ? Math.round((c.opens / c.success_count) * 100) : 0;
                  return (
                    <tr key={c.id} className="border-t border-zinc-800/60">
                      <td className="py-2 pr-3 max-w-[240px]">
                        <p className="font-medium truncate">{c.title}</p>
                        <p className="text-xs text-zinc-500 truncate">{c.body}</p>
                        {c.link && <p className="text-[10px] text-sky-500/80 truncate">🔗 {c.link}</p>}
                      </td>
                      <td className="py-2 pr-3 text-xs text-zinc-500 whitespace-nowrap">{new Date(c.sent_at).toLocaleString("az-AZ", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="py-2 pr-3 text-right text-emerald-400">{c.success_count}</td>
                      <td className="py-2 pr-3 text-right text-red-400/80">{c.failure_count}</td>
                      <td className="py-2 pr-3 text-right text-sky-400">{c.opens}</td>
                      <td className="py-2 text-right text-zinc-400">{ctr}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
